import React from 'react';
import { useIntl } from 'react-intl';

import Box from 'carbon-react/lib/components/box';

import { CombinedSubEntityForecastData } from '@futrli/views/combiners/subEntityForecastsCombiner';
import { WizardRequiredFieldInfo } from '@futrli/ui-components/components/sidebar/wizard/wizard-required-field-info/wizard-required-field-info';

import { ConsolidatedForecastDatasource } from '../form-elements/consolidated-forecast-datasource/consolidated-forecast-datasource';
import { ForecastDatasourceProps } from '../form-elements/forecast-datasource';
import { ForecastFormErrors } from '../../types';
import { ForecastFrom, ForecastFromProps } from '../form-elements/forecast-from';
import { ForecastName, ForecastNameProps } from '../form-elements/forecast-name';
import { ForecastSourceForecast, ForecastSourceForecastProps } from '../form-elements/forecast-source-forecast';
import { IconMessage } from '../form-elements/icon-message/icon-message';
import { NewConsolidatedForecastDataSource, OrgOrConForecastListItem } from '../../../dashboard/types';
import { SubEntityForecasts } from '../form-elements/sub-entity-forecasts/sub-entity-forecasts';

import styles from '../form-elements/elements.scss';

interface CreateNewConsolidatedBudgetFormProps {
  datasource: NewConsolidatedForecastDataSource | '';
  errors?: ForecastFormErrors;
  forecasts?: OrgOrConForecastListItem[];
  from: string;
  name: string;
  onChangeDatasource: ForecastDatasourceProps['onChange'];
  onChangeFrom: ForecastFromProps['onChange'];
  onChangeName: ForecastNameProps['onChange'];
  onChangeSourceForecast: ForecastSourceForecastProps['onChange'];
  onChangeSourceSubEntityForecast: (orgId: string, forecastId: string) => void;
  onChangeSourceSubEntity: (orgId: string) => void;
  revalidate: () => void;
  sourceForecast?: string;
  sourceSubEntityForecasts: Record<string, string>;
  subEntityForecasts?: CombinedSubEntityForecastData;
}

export const CreateNewConsolidatedBudgetForm = ({
  datasource,
  errors = {},
  forecasts,
  from,
  name,
  sourceForecast,
  onChangeDatasource,
  onChangeFrom,
  onChangeName,
  onChangeSourceForecast,
  onChangeSourceSubEntityForecast,
  onChangeSourceSubEntity,
  sourceSubEntityForecasts,
  subEntityForecasts
}: CreateNewConsolidatedBudgetFormProps) => {
  const intl = useIntl();

  return (
    <Box className={styles.carbonFormFixes}>
      <WizardRequiredFieldInfo />
      <ForecastName
        value={name}
        onChange={onChangeName}
        label={intl.formatMessage({ id: 'predict.create_forecast.budget_name', defaultMessage: 'Budget name' })}
        error={errors.name?.message}
      />
      <ForecastFrom
        value={from}
        onChange={onChangeFrom}
        label={intl.formatMessage({ id: 'common.start_date', defaultMessage: 'Start date' })}
        error={errors.from?.message}
      />
      <IconMessage mb={3}>
        {intl.formatMessage({
          id: 'predict.create_forecast.duration_is_one_year_from_start_date',
          defaultMessage: 'Duration is 1 year from the start date'
        })}
      </IconMessage>
      <ConsolidatedForecastDatasource
        type="budget"
        value={datasource}
        onChange={onChangeDatasource}
        error={errors.datasource?.message}
      />

      {datasource === NewConsolidatedForecastDataSource.CREATE_FROM_EXISTING && (
        <ForecastSourceForecast
          type="budget"
          value={sourceForecast ?? ''}
          onChange={onChangeSourceForecast}
          error={errors.sourceForecast?.message}
          forecasts={forecasts}
        />
      )}

      {datasource === NewConsolidatedForecastDataSource.CREATE_FROM_EXISTING_SUB_ENTITY_FORECASTS && (
        <SubEntityForecasts
          errors={errors}
          onChangeSourceSubEntity={onChangeSourceSubEntity}
          onChangeSourceSubEntityForecast={onChangeSourceSubEntityForecast}
          subEntityForecasts={subEntityForecasts}
          sourceSubEntityForecasts={sourceSubEntityForecasts}
          type="budget"
        />
      )}
    </Box>
  );
};
